import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { yupResolver } from '@hookform/resolvers/yup';
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import { useAuth } from "../../_auth/useAuth";
import { authenticationApi } from "../../_api/auth.api";
import { AuthRolesEnum } from "../../_appData";
import {
    Form, FormTitle, FormSectionHeader,
    ButtonBar, DaButton,
    DaTextField, DaPasswordField, DaNumberFormatField, DaAutocompleteField,
    MessageBox, MsgBoxType,
    NumberFormatType, Toast,
} from "../_shared";
import { SettingsPaper } from "./AccountSettings.styles";
import { useLookupOptions } from "./hooks/useLookupOptions";
import { useCustomerData } from "./hooks/useCustomerData";
import { getAccountSettingsSchema, getDefaultFormValues } from "./constants";
import { formatNaicsOptions, formatStateOptions, FieldProps } from "./utils/formHelpers";
import { IAccountSettings } from "./types";

export const AccountSettingsView: React.FC = () => {
    const navigate = useNavigate();
    const { user, getAccessToken, userHasPrivilege } = useAuth();

    // Check if user is a Licensee
    const isLicensee = userHasPrivilege([AuthRolesEnum.Licensee]);
    const isBroker = userHasPrivilege([AuthRolesEnum.ListAdmin]);
    const isListOwner = userHasPrivilege([AuthRolesEnum.ListOwner]);

    const [showProgress, setShowProgress] = useState<boolean>(false);
    const [msgBoxOpen, setMsgBoxOpen] = useState<boolean>(false);
    const [msgBoxProps, setMsgBoxProps] = useState<{
        title?: string;
        message: string;
        type?: MsgBoxType;
    }>({ message: "" });
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: 'success' | 'error' | 'info' | 'warning';
    }>({
        open: false,
        message: '',
        severity: 'info'
    });

    const schema = useMemo(() => getAccountSettingsSchema(), []);
    const { naicsOptions, stateOptions, stateSearch, naicsSearch } = useLookupOptions();
    const loadedUserIdRef = useRef<string | number | null>(null);

    const {
        control,
        handleSubmit,
        formState: { errors },
        setValue,
    } = useForm<IAccountSettings>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: getDefaultFormValues(),
    });

    const showErrorDialog = useCallback((title: string, message: string) => {
        setMsgBoxProps({ title, message, type: "error" });
        setMsgBoxOpen(true);
    }, []);

    const { getCustomerDataByEmail } = useCustomerData({
        setValue,
        stateSearch,
        naicsSearch,
        onError: showErrorDialog
    });

    useEffect(() => {
        if (!user) return;

        // Set user fields from auth context (getCustomerDataByEmail overwrites with full API data for non-Licensee)
        const authUser = user as { first_name?: string; last_name?: string; email?: string; phone?: string; phone_number?: string; privileges?: string[] };
        setValue("first_name", authUser.first_name ?? "");
        setValue("last_name", authUser.last_name ?? "");
        setValue("email", authUser.email ?? "");
        setValue("phone_number", authUser.phone_number ?? authUser.phone ?? "");

        const accountType = user.privileges?.length
            ? user.privileges.join(", ")
            : "No Role Assigned";
        setValue("account_type", accountType);

        // Only fetch company data once per user if user is not a Licensee (also populates last_name, phone from API)
        if (user.id && !isLicensee && loadedUserIdRef.current !== user.id) {
            loadedUserIdRef.current = user.id;
            getCustomerDataByEmail(user.id);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.id, isLicensee]);

    const handleCancel = () => {
        navigate("/pafs");
    };

    const handleSave = async (data: IAccountSettings) => {
        setShowProgress(true);

        try {
            // Only call password change API if both old and new passwords are provided
            if (data.old_password && data.new_password) {
                const authToken = await getAccessToken();
                const resp = await authenticationApi.change_password(
                    {
                        old_password: data.old_password,
                        new_password: data.new_password,
                    },
                    authToken
                );

                if (resp && resp.success) {
                    // Clear password fields on success
                    setValue("old_password", "");
                    setValue("new_password", "");
                    setSnackbar({
                        open: true,
                        message: "Password updated successfully",
                        severity: "success"
                    });
                } else {
                    // Show error dialog on failure
                    const errorMessage = resp?.error?.join("\n") || resp?.message || "Failed to update password. Try again.";
                    showErrorDialog("Password Update Failed", errorMessage);
                }
            } else {
                // If no password change, just show success (for other account updates)
                setSnackbar({
                    open: true,
                    message: "Account updated successfully",
                    severity: "success"
                });
            }
        } catch (err: any) {
            // Show error dialog on exception
            const errorMessage = err?.message || "Failed to update password. Try again.";
            showErrorDialog("Password Update Failed", errorMessage);
        } finally {
            setShowProgress(false);
        }
    };

    const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') return;
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const handleMsgBoxClose = () => {
        setMsgBoxOpen(false);
    };

    const fieldProps: FieldProps = { control, errors, readOnly: true };

    return (
        <Box sx={{ width: '100%' }}>
            <SettingsPaper elevation={3}>
                <FormTitle title="Account Settings" />

                <Form onSubmit={handleSubmit(handleSave)}>
                    <Grid container spacing={2}>
                        {/* Account Information */}
                        <Grid item xs={12} sm={6}>
                            <DaTextField
                                {...fieldProps}
                                name="first_name"
                                label="First Name"
                                required
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DaTextField
                                {...fieldProps}
                                name="last_name"
                                label="Last Name"
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <DaTextField
                                {...fieldProps}
                                name="email"
                                label="Email"
                                required
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <DaNumberFormatField
                                {...fieldProps}
                                name="phone_number"
                                label="Phone Number"
                                numberFormat={NumberFormatType.US_Phone}
                                required
                            // readOnly={showProgress}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <DaTextField
                                {...fieldProps}
                                name="account_type"
                                label="Account Type"
                                required
                            />
                        </Grid>

                        {/* Company Information - Hidden for Licensee */}
                        {!isLicensee && (
                            <>
                                <Grid item xs={12}>
                                    <FormSectionHeader title="Company Information" />
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <DaTextField {...fieldProps} name="company__company_name" label="Company Name" />
                                </Grid>
                                {!isBroker && <>
                                    <Grid item xs={12} md={12}>
                                        <DaTextField {...fieldProps} name="company__parent_company_name" label="Parent Company Name" />
                                    </Grid>
                                    <Grid item xs={12} md={12}>
                                        <DaTextField {...fieldProps} name="company__dba_name" label="DBA Name" />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <DaNumberFormatField
                                            {...fieldProps}
                                            name="company__usps_mailer_id"
                                            label="USPS Mailer ID"
                                            numberFormat={NumberFormatType.MAILER_ID}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={4}>
                                        <DaNumberFormatField
                                            {...fieldProps}
                                            name="company__company_registration_id"
                                            label="Company Registration ID"
                                            numberFormat={NumberFormatType.CR_ID}
                                        />
                                    </Grid></>}
                                <Grid item xs={12} md={4}>
                                    <DaAutocompleteField
                                        {...fieldProps}
                                        name="company__naics_code"
                                        label="NAICS Code"
                                        options={formatNaicsOptions(naicsOptions)}
                                        noOptionsText="Type your industry category to find the NAICS"
                                    />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <DaTextField {...fieldProps} name="company__company_website" label="Company Website" />
                                </Grid>
                                {!isListOwner && <Grid item xs={12} md={4}>
                                    <DaTextField {...fieldProps} name="company__federal_tax_id" label="Federal Tax ID" />
                                </Grid>
                                }
                                {/* Company Address */}
                                <Grid item xs={12} md={12}>
                                    <FormSectionHeader title="Company Address" />
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <DaTextField {...fieldProps} name="company__address1" label="Address Line 1" />
                                </Grid>
                                <Grid item xs={12} md={12}>
                                    <DaTextField {...fieldProps} name="company__address2" label="Address Line 2" />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <DaTextField {...fieldProps} name="company__city" label="City" />
                                </Grid>
                                <Grid item xs={12} md={4}>
                                    <DaAutocompleteField
                                        {...fieldProps}
                                        name="company__state"
                                        label="State"
                                        options={formatStateOptions(stateOptions)}
                                    />
                                </Grid>
                                <Grid item xs={12} md={2}>
                                    <DaTextField {...fieldProps} name="company__postal_code" label="Postal Code" />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <DaTextField {...fieldProps} name="company__urbanization" label="Urbanization" />
                                </Grid>
                            </>
                        )}

                        {/* Password Update */}
                        <Grid item xs={12}>
                            <FormSectionHeader title="Password Update" />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DaPasswordField
                                {...fieldProps}
                                name="old_password"
                                label="Old Password"
                                readOnly={showProgress}
                                showPasswordToggle
                            />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <DaPasswordField
                                {...fieldProps}
                                name="new_password"
                                label="New Password"
                                readOnly={showProgress}
                                showPasswordToggle
                            />
                        </Grid>
                    </Grid>

                    <ButtonBar buttonAlignment="alignRight">
                        <DaButton
                            buttonStyle="secondary"
                            type="button"
                            onClick={handleCancel}
                            disabled={showProgress}
                        >
                            Cancel
                        </DaButton>
                        <DaButton
                            buttonStyle="primary"
                            type="submit"
                            disabled={showProgress}
                        >
                            {showProgress ? "Saving..." : "Save"}
                        </DaButton>
                    </ButtonBar>
                </Form>
            </SettingsPaper>

            <Toast
                open={snackbar.open}
                message={snackbar.message}
                severity={snackbar.severity}
                onClose={handleSnackbarClose}
            />

            <MessageBox
                isOpen={msgBoxOpen}
                onClose={handleMsgBoxClose}
                message={msgBoxProps.message}
                title={msgBoxProps.title}
                type={msgBoxProps.type}
            />
        </Box>
    );
};
